import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import resumeProcess from "../../../assets/lotties/job-list/resume-parsing.json";
import resumeUpload from "../../../assets/lotties/job-list/resume-uploading.json";
import { convertBase64, setItem } from "../../../helpers/data_management";
import { updateCurrentSalaryDialog } from "../../../redux/actions/dialog_action";
import { getAshleyRecommendedJobs } from "../../../redux/actions/job_action";
import {
  checkResumeParsedStatus,
  newGetUserInfo,
  newResumeUpload,
  updateResumeBuildingStatus,
  updateResumeDoneStatus,
  updateResumeProgressPopupStatus,
  updateResumeUploadingStatus,
  updateUser,
} from "../../../redux/actions/user_action";
import * as jobTypes from "../../../redux/types/job_type";
import * as userTypes from "../../../redux/types/user_type";
import { BoxContainer, GreyText, LottieStyled, RoundedButton } from "../styles";
import {
  ButtonContainer,
  CardContainer,
  CardHeaderText,
  CardSubheadingText,
  CheckIcon,
  DragResumeElement,
  DropzoneContainer,
  HeaderContainer,
  HeaderText,
  InputStyled,
  LinkStyled,
  ResumeFailed,
  ResumeFailedText,
  ResumeFormatText,
  ResumeInReviewContainer,
  ResumeRejectedContainer,
  ResumeWrapper,
  SubheadingText,
} from "./styles";

export default function ForYouResume(props) {
  const { handleTabChange } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const user = useSelector((state) => state.user?.user);
  const jobs = useSelector((state) => state.jobs?.forYouJobs);

  const resumeExist = user?.resume?.length > 0 || user?.state != "no_resume";
  const resumeStatus = user?.state;

  const showDropzone = !resumeExist;
  const showRejected = resumeStatus === "rejected" && resumeExist;
  const showInReview =
    (resumeStatus === "in_review" && resumeExist) ||
    ((resumeStatus === "approved" || resumeStatus === "in_review") &&
      jobs.length === 0);

  useEffect(() => {
    if (
      localStorage.getItem("showCurrentSalaryDialogAfterJump") === "1" &&
      user?.id
    ) {
      const dontShow = localStorage.getItem(
        `currentSalaryDialogDontShow_${user.id}`
      );
      if (!user.currentSalary && !dontShow) {
        dispatch(updateCurrentSalaryDialog(true));
        localStorage.removeItem("showCurrentSalaryDialogAfterJump");
      } else {
        localStorage.removeItem("showCurrentSalaryDialogAfterJump");
      }
    }
  }, [dispatch, user]);

  async function handleCvFileChange(file, autoGenerated = false) {
    if (!file) return;

    if ((file.size / 1024 / 1024).toFixed(4) > 2) {
      setValidResume(false);
      setInvalidSize(true);
      return;
    }

    if (
      !(
        file.name?.includes(".docx") ||
        file.name?.includes(".doc") ||
        file.name?.includes(".pdf")
      )
    ) {
      setValidResume(false);
      setInvalidSize(false);
      return;
    }

    // Converting PDF file to base64
    convertBase64(file).then((cvFile) => {
      // Trigger graphQL to upload cvFile
      let params = {
        resume: cvFile,
        canCancel: autoGenerated,
        origin: "profile",
        user_resume: user.resume,
        autoGenerated: autoGenerated,
        state: autoGenerated ? "approved" : "review",
      };

      // Show progress popup
      dispatch(updateResumeProgressPopupStatus(true));

      // Hide building
      dispatch(updateResumeBuildingStatus(false));

      // Show uploading
      dispatch(updateResumeUploadingStatus(true));

      dispatch(newResumeUpload(params)).then((response) => {
        // Hide uploading
        dispatch(updateResumeUploadingStatus(false));

        if (response.type === "UPDATE_USER_CV_SUCCEED") {
          // Show done then hide done after 2 seconds
          dispatch(updateResumeDoneStatus(true));
          setTimeout(() => {
            dispatch(updateResumeProgressPopupStatus(false));
            dispatch(updateResumeDoneStatus(false));
          }, 2000);

          const message = (
            <>
              <span>{t("jobs.resume_updated")} </span>
              <GreyText>{t("jobs.resume_reviewing")}</GreyText>
            </>
          );

          toast.success(message, {
            timer: 3000,
          });

          // Update redux user information
          dispatch(updateUser(response.user));

          // Refetch user info after 5 minutes
          setTimeout(() => {
            refetchUserInfo(response.user);
          }, 300500);

          // Update local information
          setItem("USER_PROFILE", JSON.stringify(response.user));
        }
      });
    });
  }

  function refetchUserInfo(user) {
    const inputTime = dayjs(user?.uploadResumeAt);
    const currentTime = dayjs();
    const timePlusFiveMinutes = inputTime.add(5, "minute");

    if (timePlusFiveMinutes.isAfter(currentTime)) {
      dispatch(checkResumeParsedStatus()).then((response) => {
        if (response.type === userTypes.CHECK_RESUME_PARSED_STATUS_SUCCEED) {
          dispatch(newGetUserInfo()).then((res) => {
            const latestResumeState = res.payload?.user?.state;

            const jobKey = new Date().getTime();

            const params = {
              currentLoadingJobsKey: jobKey,
              first: 50,
              last: null,
              startCursor: null,
              endCursor: null,
            };

            dispatch(getAshleyRecommendedJobs(params)).then((res) => {
              if (
                res?.type === jobTypes.FETCH_ASHLEY_RECOMMENDED_JOBS_SUCCEED &&
                latestResumeState === "approved"
              ) {
                const message = <span>{t("jobs.jobs_updated")}</span>;

                toast.success(message, {
                  timer: 3000,
                });
              }
            });
          });
        }
      });
    }
  }

  function handleFileChange(e) {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleCvFileChange(e.target.files[0]);
      e.target.value = null;
    }
  }

  function handleExploreAllJobs() {
    handleTabChange("job-list");
  }

  return (
    <ResumeWrapper>
      {showDropzone && (
        <ResumeDropzone
          handleFileChange={handleFileChange}
          handleCvFileChange={handleCvFileChange}
          handleExploreAllJobs={handleExploreAllJobs}
        />
      )}
      {showInReview && (
        <ResumeInReview
          refetchUserInfo={refetchUserInfo}
          handleExploreAllJobs={handleExploreAllJobs}
        />
      )}
      {showRejected && (
        <ResumeRejected
          handleFileChange={handleFileChange}
          handleExploreAllJobs={handleExploreAllJobs}
        />
      )}
    </ResumeWrapper>
  );
}

function ResumeDropzone(props) {
  const { handleFileChange, handleCvFileChange, handleExploreAllJobs } = props;

  const user = useSelector((state) => state.user?.user);
  const { t } = useTranslation();

  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  // handle drag events
  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }

  // triggers when file is dropped
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (user?.state === "no_resume") {
        handleCvFileChange(e.dataTransfer.files[0]);
      }
    }
  }

  function handleBrowseFiles() {
    inputRef.current.click();
  }

  return (
    <>
      <HeaderContainer>
        <HeaderText>{t("jobs.resume_feed_title")}</HeaderText>
        <SubheadingText>
          {t("jobs.resume_feed_subtitle")}
        </SubheadingText>
      </HeaderContainer>

      <CardContainer>
        <BoxContainer flexDirection="column" alignItems="center">
          <CardHeaderText>{t("jobs.resume_better_matches_title")}</CardHeaderText>
          <CardSubheadingText>{t("jobs.resume_upload_prompt")}</CardSubheadingText>
        </BoxContainer>
        <DropzoneContainer
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <LottieStyled
            options={{
              loop: true,
              autoplay: true,
              animationData: resumeUpload,
            }}
            width={"104px"}
            height={"80px"}
            style={{ margin: "0 0 1rem" }}
          />
          <RoundedButton
            variant="filled"
            size="medium"
            onClick={handleBrowseFiles}
          >
            {t("jobs.resume_browse_files")}
          </RoundedButton>
          <ResumeFormatText>
            {t("jobs.resume_supported_formats")}
          </ResumeFormatText>
        </DropzoneContainer>
        <RoundedButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={handleExploreAllJobs}
        >
          {t("jobs.resume_explore_all_jobs")}
        </RoundedButton>
      </CardContainer>
      <InputStyled
        ref={inputRef}
        accept=".pdf,.docx,.doc"
        type="file"
        onChange={handleFileChange}
      />
      {dragActive && (
        <DragResumeElement
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
      )}
    </>
  );
}

function ResumeInReview(props) {
  const { refetchUserInfo, handleExploreAllJobs } = props;
  const { t } = useTranslation();

  return (
    <>
      <CardContainer>
        <ResumeInReviewContainer>
          <CardHeaderText>
            <CheckIcon /> {t("jobs.resume_upload_success")}
          </CardHeaderText>
          <LottieStyled
            options={{
              loop: true,
              autoplay: true,
              animationData: resumeProcess,
            }}
            width={"100px"}
            height={"100px"}
          />
          <CardSubheadingText>
            {t("jobs.resume_in_review_message")}
          </CardSubheadingText>
        </ResumeInReviewContainer>
        <ButtonContainer>
          <RoundedButton
            variant="outlined"
            color="secondary"
            onClick={handleExploreAllJobs}
          >
            {t("jobs.resume_explore_all_jobs")}
          </RoundedButton>
          <RoundedButton variant="filled" onClick={refetchUserInfo}>
            {t("jobs.resume_refresh_page")}
          </RoundedButton>
        </ButtonContainer>
      </CardContainer>
    </>
  );
}

function ResumeRejected(props) {
  const { handleFileChange, handleExploreAllJobs } = props;
  const { t } = useTranslation();

  const inputRef = useRef(null);

  function handleReupload() {
    inputRef.current.click();
  }

  return (
    <>
      <CardContainer>
        <ResumeRejectedContainer>
          <CardHeaderText>{t("jobs.resume_no_matches_title")}</CardHeaderText>
          <BoxContainer flexDirection="column" alignItems="center" gap="1rem">
            <ResumeFailed />
            <CardSubheadingText>
              {t("jobs.resume_no_matches_message")}
            </CardSubheadingText>
          </BoxContainer>
          <ResumeFailedText>
            {t("jobs.resume_no_matches_contact")}{" "}
            <LinkStyled
              target="_blank"
              rel="noopener"
              href="mailto:hello@hiredly.com?subject=No Job Recommendations Available&body=Dear Hiredly,<br><br>I am unable to view any job recommendations.<br><br>Please could you assist me with:"
            >
              hello@hiredly.com
            </LinkStyled>
          </ResumeFailedText>
        </ResumeRejectedContainer>
        <ButtonContainer>
          <RoundedButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleExploreAllJobs}
          >
            {t("jobs.resume_explore_all_jobs")}
          </RoundedButton>
          <RoundedButton
            variant="filled"
            color="secondary"
            size="small"
            onClick={handleReupload}
          >
            {t("jobs.resume_reupload")}
          </RoundedButton>
        </ButtonContainer>
      </CardContainer>
      <InputStyled
        ref={inputRef}
        accept=".pdf,.docx,.doc"
        type="file"
        onChange={handleFileChange}
      />
    </>
  );
}
